import { type FormEvent, useEffect, useState } from 'react';
import { api } from '../api';

// Sign-in is email and nothing else.
//
// For three weeks it was Telegram only (#2141), because the relay refused our
// domain (#1826). A code to an external Proton address was delivered on
// 2026-10-02, so email is back and Telegram is gone: the API is its own product
// with its own customers, and a developer buying it has no reason to hold an
// account with one of our bots.

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  return (
    <main className="signin">
      <div className="signin-card">
        <div className="brand">
          <img className="brand-mark" src="/favicon.png" alt="" />
          <span>
            <h1>Imaginai</h1>
            <span className="brand-sub">Developer API</span>
          </span>
        </div>

        <div className="panel">
          <EmailSignIn onSignedIn={onSignedIn} />
        </div>
      </div>
    </main>
  );
}

function EmailSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A code that does not arrive is the common case — spam folder, typo, slow
  // relay — so there is a way to ask for another without starting over.
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const request = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.requestEmailCode(email.trim());
      setSent(true);
      // Matches the server's per-address cooldown (#1817). Offering the button
      // sooner only produces a refusal the person cannot act on.
      setResendIn(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.verifyEmailCode(email.trim(), code.trim());
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not accepted');
    } finally {
      setBusy(false);
    }
  };

  if (!sent) {
    return (
      <form onSubmit={request}>
        <h3>Sign in</h3>
        <p className="muted small">We email a six-digit code. No password to lose.</p>
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        {error ? <p className="error">{error}</p> : null}
        <p>
          <button type="submit" disabled={busy || !email.trim()}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={verify}>
      <h3>Enter the code</h3>
      <p className="muted small">
        Sent to {email}. It is good for five minutes; the link in the email works too.
      </p>
      <label htmlFor="code">Code</label>
      <input
        id="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onChange={(event) => setCode(event.target.value)}
        required
      />
      {error ? <p className="error">{error}</p> : null}
      <div className="row" style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || !code.trim()}>
          {busy ? 'Checking…' : 'Sign in'}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={busy || resendIn > 0}
          onClick={() => void request()}
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : 'Send another'}
        </button>
      </div>
      <p className="panel-note">
        <button
          type="button"
          className="link"
          style={{ color: 'var(--muted)' }}
          onClick={() => {
            setSent(false);
            setCode('');
            setError('');
          }}
        >
          Use another email
        </button>
      </p>
    </form>
  );
}
